import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle, XCircle, CreditCard } from 'lucide-react';
import Button from './Button';

export interface StripePopupProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  type?: 'success' | 'error';
}

const StripePopup: React.FC<StripePopupProps> = ({
  isOpen,
  onClose,
  message,
  type = 'error',
}) => {
  const handleConfirm = () => {
    onClose();
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'error':
        return <XCircle className="w-6 h-6 text-red-500" />;
      default:
        return <CreditCard className="w-6 h-6 text-blue-500" />;
    }
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return 'border-green-200 bg-green-50/50';
      case 'error':
        return 'border-red-200 bg-red-50/50';
      default:
        return 'border-blue-200 bg-blue-50/50';
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'success':
        if (message.toLowerCase().includes('cancel') || message.toLowerCase().includes('remain active until')) {
          return 'Subscription Cancelled';
        }
        return 'Payment Successful';
      case 'error':
        return 'Payment Error';
      default:
        return 'Stripe Notification';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ 
              type: "spring", 
              damping: 20, 
              stiffness: 300,
              duration: 0.3 
            }}
            className={`
              relative w-full max-w-md mx-auto
              bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl
              border border-white/20 overflow-hidden
              ${getTypeStyles()}
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />

            <div className="relative p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  {getIcon()}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {getTitle()}
                  </h3>
                  
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {message}
                  </p>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleConfirm}
                >
                  OK
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StripePopup;